import { useState } from 'react';
import type { PanelDePlataforma } from '@medchoice/shared';
import { administracion } from '../../api/administracion';
import { usePedido } from '../../api/usePedido';
import { irA } from '../../router';
import {
  Cargando, FalloDeCarga, GraficoAnillo, GraficoBarras, GraficoLinea, Icono, Kpi, PanelGrafico, Selector, Tarjeta,
} from '../../ui';
import { SeccionPanel } from '../../ui/layout/PaginaPanel';
import { pesos, porcentaje } from '../../ui/format';

const PERIODOS = [
  { valor: '4', texto: 'Últimas 4 semanas' }, { valor: '8', texto: 'Últimas 8 semanas' }, { valor: '12', texto: 'Últimas 12 semanas' },
];

export function PanelGeneral() {
  const [semanas, setSemanas] = useState(8);
  const pedido = usePedido(() => administracion.panel(semanas), [semanas]);
  const p = pedido.datos;
  return (
    <SeccionPanel titulo="Panel general" descripcion={`Toda la plataforma en las últimas ${semanas} semanas`}
      acciones={
        <div className="accion-selector">
          <Selector aria-label="Período" value={String(semanas)} opciones={PERIODOS} onChange={(e) => setSemanas(Number(e.target.value))} />
        </div>
      }>
      {pedido.estado === 'cargando' && !p && <Cargando />}
      {pedido.estado === 'error' && <FalloDeCarga mensaje={pedido.error ?? ''} alReintentar={pedido.recargar} />}
      {p && <Tablero p={p} />}
    </SeccionPanel>
  );
}

function Tablero({ p }: { p: PanelDePlataforma }) {
  const ingreso = p.suscripciones + p.comision_del_mes;
  const registrados = p.embudo[0]?.profesionales ?? 0;
  return (
    <div className="pila">
      <div className="kpis">
        <Kpi etiqueta="Profesionales" valor={p.profesionales} icono="stethoscope" pista={`${p.verificados} verificados`} />
        <Kpi etiqueta="Pacientes" valor={p.pacientes} icono="users" />
        <Kpi etiqueta="Turnos del período" valor={p.turnos} icono="calendar" pista={`${p.atendidos} atendidos`} />
        <Kpi etiqueta="Volumen facturado" valor={pesos(p.volumen_facturado)} icono="wallet" pista="Por los profesionales" />
      </div>

      {(p.pendientes_de_verificacion > 0 || p.reclamos_abiertos > 0) && (
        <Tarjeta>
          <h3 className="titulo-tarjeta">Pendientes de atención</h3>
          <div className="pendientes">
            {p.pendientes_de_verificacion > 0 && (
              <button type="button" className="pendiente pendiente--aviso" onClick={() => irA('/admin/profesionales')}>
                <Icono nombre="shield-check" tamano={18} />
                {p.pendientes_de_verificacion === 1 ? '1 matrícula' : `${p.pendientes_de_verificacion} matrículas`} para verificar
              </button>
            )}
            {p.reclamos_abiertos > 0 && (
              <button type="button" className="pendiente pendiente--peligro" onClick={() => irA('/admin/reclamos')}>
                <Icono nombre="flag" tamano={18} />
                {p.reclamos_abiertos === 1 ? '1 reclamo' : `${p.reclamos_abiertos} reclamos`} sin resolver
              </button>
            )}
          </div>
        </Tarjeta>
      )}

      <Tarjeta>
        <div className="ingresos">
          <div>
            <h3>Ingresos de MedChoice</h3>
            <p className="nota">Suscripciones más la comisión de servicio sobre las señas</p>
          </div>
          <div className="ingresos__total">
            <strong>{pesos(ingreso)}</strong>
            <span className="nota">Ingreso de este mes</span>
          </div>
        </div>
        <div className="ingresos__partes">
          <div>
            <span>Suscripciones</span>
            <strong>{pesos(p.suscripciones)}</strong>
            <span>{p.en_plan_pago} de {p.profesionales} profesionales en plan pago</span>
          </div>
          <div>
            <span>Comisión por señas</span>
            <strong>{pesos(p.comision_del_mes)}</strong>
            <span>Sobre {pesos(p.senas_del_mes)} de señas acreditadas este mes</span>
          </div>
          <div>
            <span>Conversión a plan pago</span>
            <strong>{porcentaje(p.profesionales ? Math.round((p.en_plan_pago / p.profesionales) * 100) : 0)}</strong>
            <span>
              El plan de negocios proyecta {porcentaje(p.proyeccion.conversion * 100)} sobre {p.proyeccion.profesionales} profesionales
            </span>
          </div>
        </div>
      </Tarjeta>

      <PanelGrafico titulo="Embudo de profesionales" subtitulo="Cada etapa cuenta solo a los que pasaron la anterior">
        <GraficoBarras color="var(--success)"
          formatear={(v) => `${v} · ${porcentaje(registrados ? Math.round((v / registrados) * 100) : 0)}`}
          items={p.embudo.map((e) => ({ etiqueta: e.etapa, valor: e.profesionales }))} />
      </PanelGrafico>

      <div className="graficos">
        <PanelGrafico titulo="Turnos por semana en toda la plataforma" subtitulo="Cada punto es una semana cerrada, con todos los estados">
          <GraficoLinea datos={p.por_semana.map((s) => s.turnos)} etiquetas={p.por_semana.map((s) => s.etiqueta)} />
        </PanelGrafico>
        <PanelGrafico titulo="Turnos por especialidad" subtitulo="Dónde está la demanda real">
          <GraficoBarras items={p.por_especialidad.map((e) => ({ etiqueta: e.etiqueta, valor: e.turnos }))} />
        </PanelGrafico>
        <PanelGrafico titulo="Profesionales por plan" subtitulo="De qué se compone el ingreso recurrente">
          <GraficoBarras color="var(--success)" items={p.por_plan.map((e) => ({ etiqueta: e.etiqueta, valor: e.profesionales }))} />
        </PanelGrafico>
        <PanelGrafico titulo="Resultado de los turnos" subtitulo="Turnos del período que ya ocurrieron">
          <GraficoAnillo centro={{ valor: p.atendidos + p.ausentes + p.cancelados, texto: 'turnos' }} partes={[
            { etiqueta: 'Atendidos', valor: p.atendidos, color: 'var(--success)' },
            { etiqueta: 'Cancelados', valor: p.cancelados, color: 'var(--ink-300)' },
            { etiqueta: 'No asistieron', valor: p.ausentes, color: 'var(--danger)' },
          ]} />
        </PanelGrafico>
      </div>
    </div>
  );
}
